import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

const dict = {
  en: {
    "nav.home": "Home",
    "nav.how": "How It Works",
    "nav.services": "Services",
    "nav.findTrainer": "Find a Trainer",
    "nav.families": "For Families",
    "nav.trainers": "For Trainers",
    "nav.testimonials": "Stories",
    "nav.faq": "FAQ",
    "nav.about": "About the Owner",
    "nav.contact": "Request Training",
    "nav.login": "Sign In",
    "nav.dashboard": "Dashboard",
    "nav.logout": "Sign Out",

    "brand.tag": "Psychology-based dog training",
    "hero.title": "Training built around your dog's mind, your family's rhythm, and your real life.",
    "hero.sub": "Oh My Dog! connects families across the U.S. and Mexico with highly qualified, psychology-based dog trainers — and if we don't have one near you yet, we'll bring one to you.",
    "hero.cta": "Find My Dog's Trainer",
    "hero.secondary": "Browse Trainers",

    "phrase.everyDog": "Every dog has a story.",
    "phrase.understanding": "Training starts with understanding.",
    "phrase.chaos": "From chaos to calm.",
    "phrase.match": "We match your dog with the right human.",

    "section.different.kicker": "Why Oh My Dog!",
    "section.different.title": "Not just obedience — psychology, family, and lifelong wins.",
    "section.different.sub": "We build training around how your dog actually learns, how your family lives, and what calm really looks like in your home.",

    "section.psych.kicker": "Psychology-based",
    "section.psych.title": "We start with the dog behind the behavior.",
    "section.psych.sub": "Reactive isn't bad. Anxious isn't broken. We use evidence-based behavior science, positive reinforcement, and a deep understanding of your dog's emotional world.",

    "services.title": "Training options for every family",
    "services.sub": "Real plans for real homes — not cookie-cutter classes.",

    "match.title": "How trainer matching works",
    "match.sub": "Country → City → Specialty → Availability → Rating. If we don't have a trainer in your area yet, we'll bring one to you.",

    "coverage.title": "Coverage across the U.S. and Mexico",
    "coverage.sub": "Approved Oh My Dog! trainers are active in these cities, with new locations added every month.",

    "featured.title": "Featured trainers",
    "featured.sub": "Real humans. Certified, calm, and dog-obsessed.",
    "featured.viewAll": "View all trainers",

    "testimonials.title": "Happy dog stories",
    "testimonials.sub": "From chaos to calm — real wins from Oh My Dog! families.",

    "faq.title": "Frequently asked questions",

    "cta.final.title": "Ready to meet your dog's perfect human?",
    "cta.final.sub": "Tell us a little about your pup. We'll match you in minutes.",

    "form.title": "Request training",
    "form.sub": "It takes about 90 seconds. Promise.",
    "form.step": "Step",
    "form.of": "of",
    "form.next": "Next",
    "form.back": "Back",
    "form.submit": "Find My Trainer",
    "form.success": "We got it! 🐾 Your match is on the way.",

    "form.s1.title": "About your family",
    "form.s2.title": "About your dog",
    "form.s3.title": "What kind of training?",
    "form.s4.title": "Where & when",
    "form.s5.title": "Last touches",

    "form.f.parent_name": "Your name",
    "form.f.parent_email": "Email",
    "form.f.parent_phone": "Phone",
    "form.f.family_size": "Family size",
    "form.f.has_kids": "Kids in the home",
    "form.f.dog_name": "Dog's name",
    "form.f.dog_breed": "Breed",
    "form.f.dog_age": "Age",
    "form.f.dog_temperament": "Temperament",
    "form.f.service": "Service needed",
    "form.f.goals": "What's the dream outcome?",
    "form.f.country": "Country",
    "form.f.city": "City",
    "form.f.preferred_schedule": "Preferred schedule",
    "form.f.notes": "Anything else we should know?",

    "service.one_on_one": "One-on-one private",
    "service.board_train": "Board & train",
    "service.group": "Group training",
    "service.train_trainer": "Train-the-trainer (kids/family)",
    "service.behavioral": "Behavioral support",
    "service.puppy": "Puppy foundations",
    "service.reactivity": "Reactivity & anxiety",
    "service.pack_walks": "Pack walks",

    "common.loading": "Loading…",
    "common.error": "Something went wrong",
    "common.search": "Search",
    "common.filter": "Filter",
    "common.country": "Country",
    "common.city": "City",
    "common.specialty": "Specialty",
    "common.all": "All",
    "common.send": "Send",

    "login.title": "Welcome back",
    "login.sub": "Sign in to your Oh My Dog! portal.",
    "login.demo": "Demo accounts",

    "admin.title": "Admin · CRM Dashboard",
    "admin.tab.overview": "Overview",
    "admin.tab.leads": "Leads",
    "admin.tab.trainers": "Trainers",
    "admin.tab.coverage": "Coverage",

    "trainer.title": "Trainer Portal",
    "trainer.tab.leads": "Assigned Leads",
    "trainer.tab.sessions": "Sessions",
    "trainer.tab.messages": "Messages",
    "trainer.tab.profile": "Profile",

    "parent.title": "My Family Portal",
    "parent.tab.dogs": "My Dogs",
    "parent.tab.sessions": "Sessions",
    "parent.tab.homework": "Homework",
    "parent.tab.messages": "Messages",
    "parent.tab.community": "Community",
    "parent.tab.ask": "Ask a Trainer",
  },
  es: {
    "nav.home": "Inicio",
    "nav.how": "Cómo funciona",
    "nav.services": "Servicios",
    "nav.findTrainer": "Encuentra entrenador",
    "nav.families": "Para familias",
    "nav.trainers": "Para entrenadores",
    "nav.testimonials": "Historias",
    "nav.faq": "Preguntas",
    "nav.about": "Sobre la dueña",
    "nav.contact": "Solicitar entrenamiento",
    "nav.login": "Entrar",
    "nav.dashboard": "Panel",
    "nav.logout": "Salir",

    "brand.tag": "Entrenamiento canino basado en psicología",
    "hero.title": "Entrenamiento diseñado para la mente de tu perro, el ritmo de tu familia y tu vida real.",
    "hero.sub": "Oh My Dog! conecta familias en EE. UU. y México con entrenadores caninos de altísimo nivel basados en psicología — y si aún no hay uno cerca de ti, lo llevamos.",
    "hero.cta": "Encuentra el entrenador de mi perro",
    "hero.secondary": "Ver entrenadores",

    "phrase.everyDog": "Cada perro tiene una historia.",
    "phrase.understanding": "Todo entrenamiento empieza por entender.",
    "phrase.chaos": "Del caos a la calma.",
    "phrase.match": "Conectamos a tu perro con el humano correcto.",

    "section.different.kicker": "¿Por qué Oh My Dog!?",
    "section.different.title": "No solo obediencia — psicología, familia y resultados de por vida.",
    "section.different.sub": "Diseñamos planes alrededor de cómo aprende tu perro y cómo vive tu familia, no en clases genéricas.",

    "section.psych.kicker": "Basado en psicología",
    "section.psych.title": "Empezamos por el perro detrás del comportamiento.",
    "section.psych.sub": "Reactivo no es malo. Ansioso no es roto. Usamos ciencia del comportamiento, refuerzo positivo y comprensión profunda de su mundo emocional.",

    "services.title": "Opciones para cada familia",
    "services.sub": "Planes reales para casas reales — no clases tipo molde.",

    "match.title": "Cómo emparejamos entrenadores",
    "match.sub": "País → Ciudad → Especialidad → Disponibilidad → Calificación. Si no hay nadie cerca, llevamos uno a ti.",

    "coverage.title": "Cobertura en EE. UU. y México",
    "coverage.sub": "Estos son los entrenadores aprobados activos hoy. Cada mes sumamos ciudades nuevas.",

    "featured.title": "Entrenadores destacados",
    "featured.sub": "Personas reales. Certificadas, tranquilas y obsesionadas con los perros.",
    "featured.viewAll": "Ver todos",

    "testimonials.title": "Historias felices",
    "testimonials.sub": "Del caos a la calma — testimonios reales de familias Oh My Dog!",

    "faq.title": "Preguntas frecuentes",

    "cta.final.title": "¿Listos para encontrar a su humano ideal?",
    "cta.final.sub": "Cuéntanos un poco sobre tu perro. Te emparejamos en minutos.",

    "form.title": "Solicitar entrenamiento",
    "form.sub": "Toma menos de 90 segundos.",
    "form.step": "Paso",
    "form.of": "de",
    "form.next": "Siguiente",
    "form.back": "Atrás",
    "form.submit": "Encuentra mi entrenador",
    "form.success": "¡Listo! 🐾 Tu match está en camino.",

    "form.s1.title": "Sobre tu familia",
    "form.s2.title": "Sobre tu perro",
    "form.s3.title": "¿Qué tipo de entrenamiento?",
    "form.s4.title": "Dónde y cuándo",
    "form.s5.title": "Últimos detalles",

    "form.f.parent_name": "Tu nombre",
    "form.f.parent_email": "Correo",
    "form.f.parent_phone": "Teléfono",
    "form.f.family_size": "Personas en casa",
    "form.f.has_kids": "Hay niños en casa",
    "form.f.dog_name": "Nombre del perro",
    "form.f.dog_breed": "Raza",
    "form.f.dog_age": "Edad",
    "form.f.dog_temperament": "Temperamento",
    "form.f.service": "Servicio",
    "form.f.goals": "¿Cuál es el resultado soñado?",
    "form.f.country": "País",
    "form.f.city": "Ciudad",
    "form.f.preferred_schedule": "Horario preferido",
    "form.f.notes": "¿Algo más que debamos saber?",

    "service.one_on_one": "Privado uno a uno",
    "service.board_train": "Internado con entrenamiento",
    "service.group": "Clases grupales",
    "service.train_trainer": "Entrena al entrenador (niños/familia)",
    "service.behavioral": "Apoyo conductual",
    "service.puppy": "Cachorros",
    "service.reactivity": "Reactividad y ansiedad",
    "service.pack_walks": "Caminatas en manada",

    "common.loading": "Cargando…",
    "common.error": "Algo salió mal",
    "common.search": "Buscar",
    "common.filter": "Filtrar",
    "common.country": "País",
    "common.city": "Ciudad",
    "common.specialty": "Especialidad",
    "common.all": "Todos",
    "common.send": "Enviar",

    "login.title": "Bienvenido de vuelta",
    "login.sub": "Entra a tu portal Oh My Dog!",
    "login.demo": "Cuentas demo",

    "admin.title": "Admin · Tablero CRM",
    "admin.tab.overview": "Resumen",
    "admin.tab.leads": "Leads",
    "admin.tab.trainers": "Entrenadores",
    "admin.tab.coverage": "Cobertura",

    "trainer.title": "Portal del entrenador",
    "trainer.tab.leads": "Leads asignados",
    "trainer.tab.sessions": "Sesiones",
    "trainer.tab.messages": "Mensajes",
    "trainer.tab.profile": "Perfil",

    "parent.title": "Portal de la familia",
    "parent.tab.dogs": "Mis perros",
    "parent.tab.sessions": "Sesiones",
    "parent.tab.homework": "Tareas",
    "parent.tab.messages": "Mensajes",
    "parent.tab.community": "Comunidad",
    "parent.tab.ask": "Pregunta a un entrenador",
  },
};

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return window.localStorage.getItem("omd_lang") || "en";
    } catch {
      return "en";
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("omd_lang", lang);
    } catch {}
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: (k) => (dict[lang] && dict[lang][k]) || dict.en[k] || k,
    }),
    [lang]
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
